<script lang="ts">
    import {page} from '$app/stores';
    import {
        CaretDown,
        CaretRight,
        CloudCheck,
        CurrencyDollar,
        EnvelopeSimple,
        FileText,
        FlagBanner,
        GitBranch,
        GlobeHemisphereEast,
        Key,
        MagnifyingGlass,
        SignOut,
        UsersThree,
        Wrench
    } from 'phosphor-svelte';
    import {MAIL_OCR_ROUTES} from '$lib/models/mail-ocr/types';
    import {getDrawerStore} from '@skeletonlabs/skeleton';
    import {authStore} from '$lib/stores/authStore';
    import {mailOcrEnabled} from '$lib/stores/settingsStore';

    const isAdmin = authStore.isAdmin;

  $: activeUrlButton = (href: string) => ($page.url.pathname?.includes(href) ? 'bg-surface-active-token' : '');
  $: linkTextClasses = (href: string) => ($page.url.pathname?.includes(href) ? 'text-primary-500' : 'text-slate-600');
  $: linkIconClasses = (href: string) => ($page.url.pathname?.includes(href) ? 'text-tertiary-500' : 'text-slate-600');

  // exact-match helpers to avoid parent/child both being highlighted
  $: activeUrlExact = (href: string) => ($page.url.pathname === href || $page.url.pathname === `${href}/`) ? 'bg-surface-active-token' : '';
  $: linkTextExact = (href: string) => ($page.url.pathname === href || $page.url.pathname === `${href}/`) ? 'text-primary-500' : 'text-slate-600';
  $: linkIconExact = (href: string) => ($page.url.pathname === href || $page.url.pathname === `${href}/`) ? 'text-tertiary-500' : 'text-slate-600';

  const drawer = getDrawerStore();

  // submenu state: open if current route matches or toggled by user
  $: showAzure = $page.url.pathname?.includes('azure-services');
  let showAzureManual = null as boolean | null;
  $: azureOpen = showAzureManual === null ? showAzure : showAzureManual;

  $: showReports = $page.url.pathname?.includes('reports');
  let showReportsManual = null as boolean | null;
  $: reportsOpen = showReportsManual === null ? showReports : showReportsManual;

  $: showMailOcr = $page.url.pathname?.includes('mail-ocr');
  let showMailOcrManual = null as boolean | null;
  $: mailOcrOpen = showMailOcrManual === null ? showMailOcr : showMailOcrManual;

  // Sub-items match on prefix so detail pages (e.g. a document under the queue) keep their parent highlighted.
  const MAIL_OCR_LINKS = [
    { href: MAIL_OCR_ROUTES.queue, label: 'Review queue' },
    { href: MAIL_OCR_ROUTES.invoiceGenie, label: 'Invoice Genie' },
    { href: MAIL_OCR_ROUTES.failedMails, label: 'Failed mail' },
    { href: MAIL_OCR_ROUTES.insights, label: 'Insights' },
    { href: MAIL_OCR_ROUTES.rules, label: 'Rules' },
  ];

  function drawerClose(): void {
    drawer.close();
  }

  function handleLogout() {
    authStore.signOut();
  }
</script>

<section class="m-4 space-y-4 overflow-y-auto">
  <nav class="list-nav pb-5">
    <span class="text-slate-400"> Performance </span>
    <ul class="mb-5 mt-5">
      <li>
        <a href="/integrations" class="text-lg {activeUrlButton('integrations')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
          <GitBranch class={linkIconClasses('integrations')} />
          <span class="font-medium {linkTextClasses('integrations')}">Integrations</span></a
        >
      </li>
      <li class="pb-1">
        <button class="text-lg flex items-center justify-between w-full text-left {activeUrlButton('azure-services')}" on:click={() => (showAzureManual = !azureOpen)}>
          <div class="flex items-center gap-4">
            <CloudCheck class={linkIconClasses('azure-services')} />
            <span class="font-medium {linkTextClasses('azure-services')}">Azure Services</span>
          </div>
          {#if azureOpen}
            <CaretDown class={linkIconClasses('azure-services')} />
          {:else}
            <CaretRight class={linkIconClasses('azure-services')} />
          {/if}
        </button>
        {#if azureOpen}
          <ul class="ml-8 mt-2 space-y-1">
            <li>
              <a
                href="/azure-services"
                class="text-base {activeUrlExact('/azure-services')}"
                data-sveltekit-preload-data="hover"
                on:click={drawerClose}
              >
                <span class="font-medium {linkTextExact('/azure-services')}">Services List</span></a
              >
            </li>
            <li>
              <a
                href="/azure-services/visualization"
                class="text-base {activeUrlExact('/azure-services/visualization')}"
                data-sveltekit-preload-data="hover"
                on:click={drawerClose}
              >
                <span class="font-medium {linkTextExact('/azure-services/visualization')}">Services Architecture</span></a
              >
            </li>
          </ul>
        {/if}
      </li>
    </ul>
    <span class="text-slate-400"> Operations </span>
    <ul class="mb-5 mt-5">
      <li>
        <a href="/client" class="text-lg {activeUrlButton('client')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
          <UsersThree class={linkIconClasses('client')} />
          <span class="font-medium {linkTextClasses('client')}">Clients</span></a
        >
      </li>
      <ul class="mb-5 mt-5">
        <li>
          <a href="/feature-flag" class="text-lg {activeUrlButton('feature-flag')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <FlagBanner class={linkIconClasses('feature-flag')} />
            <span class="font-medium {linkTextClasses('feature-flag')}">Feature Flags</span></a
          >
        </li>
        <li class="pb-1">
          <a href="/sso" class="text-lg {activeUrlButton('sso')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <Key class={linkIconClasses('sso')} />
            <span class="font-medium {linkTextClasses('sso')}">SSO</span></a
          >
        </li>
        <li class="pb-1">
          <a href="/contract" class="text-lg {activeUrlButton('contract')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <CurrencyDollar class={linkIconClasses('contract')} />
            <span class="font-medium {linkTextClasses('contract')}">Contracts</span></a
          >
        </li>
        <li class="pb-1">
          <button class="text-lg flex items-center justify-between w-full text-left {activeUrlButton('reports')}" on:click={() => (showReportsManual = !reportsOpen)}>
            <div class="flex items-center gap-4">
              <FileText class={linkIconClasses('reports')} />
              <span class="font-medium {linkTextClasses('reports')}">Reports</span>
            </div>
            {#if reportsOpen}
              <CaretDown class={linkIconClasses('reports')} />
            {:else}
              <CaretRight class={linkIconClasses('reports')} />
            {/if}
          </button>
          {#if reportsOpen}
            <ul class="ml-8 mt-2 space-y-1">
              <li>
                <a
                  href="/reports/invoicefailedpost"
                  class="text-base {activeUrlExact('/reports/invoicefailedpost')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/invoicefailedpost')}">Invoice Failed Post</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/invoicestuckposting"
                  class="text-base {activeUrlExact('/reports/invoicestuckposting')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/invoicestuckposting')}">Invoice Stuck Posting</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/invoicefailedexport"
                  class="text-base {activeUrlExact('/reports/invoicefailedexport')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/invoicefailedexport')}">Invoice Failed Export</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/invoicestuckexporting"
                  class="text-base {activeUrlExact('/reports/invoicestuckexporting')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/invoicestuckexporting')}">Invoice Stuck Exporting</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/invoicelocked"
                  class="text-base {activeUrlExact('/reports/invoicelocked')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/invoicelocked')}">Invoice Locked</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/expenseclaimfailedpost"
                  class="text-base {activeUrlExact('/reports/expenseclaimfailedpost')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/expenseclaimfailedpost')}">Expense Claim Failed Post</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/expenseclaimstuckposting"
                  class="text-base {activeUrlExact('/reports/expenseclaimstuckposting')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/expenseclaimstuckposting')}">Expense Claim Stuck Posting</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/expenseclaimlocked"
                  class="text-base {activeUrlExact('/reports/expenseclaimlocked')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/expenseclaimlocked')}">Expense Claim Locked</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/purchaseorderfailedemailsend"
                  class="text-base {activeUrlExact('/reports/purchaseorderfailedemailsend')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/purchaseorderfailedemailsend')}">Purchase Order Failed Email Send</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/purchaseorderemaildetails"
                  class="text-base {activeUrlExact('/reports/purchaseorderemaildetails')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/purchaseorderemaildetails')}">Purchase Order Email Details</span></a
                >
              </li>
              <li>
                <a
                  href="/reports/purchaseorderlocked"
                  class="text-base {activeUrlExact('/reports/purchaseorderlocked')}"
                  data-sveltekit-preload-data="hover"
                  on:click={drawerClose}
                >
                  <span class="font-medium {linkTextExact('/reports/purchaseorderlocked')}">Purchase Order Locked</span></a
                >
              </li>
            </ul>
          {/if}
        </li>
        {#if $mailOcrEnabled}
        <li class="pb-1">
          <button class="text-lg flex items-center justify-between w-full text-left {activeUrlButton('mail-ocr')}" on:click={() => (showMailOcrManual = !mailOcrOpen)}>
            <div class="flex items-center gap-4">
              <EnvelopeSimple class={linkIconClasses('mail-ocr')} />
              <span class="font-medium {linkTextClasses('mail-ocr')}">Mail OCR</span>
            </div>
            {#if mailOcrOpen}
              <CaretDown class={linkIconClasses('mail-ocr')} />
            {:else}
              <CaretRight class={linkIconClasses('mail-ocr')} />
            {/if}
          </button>
          {#if mailOcrOpen}
            <ul class="ml-8 mt-2 space-y-1">
              {#each MAIL_OCR_LINKS as link (link.href)}
                <li>
                  <a href={link.href} class="text-base {activeUrlButton(link.href)}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
                    <span class="font-medium {linkTextClasses(link.href)}">{link.label}</span></a
                  >
                </li>
              {/each}
            </ul>
          {/if}
        </li>
        {/if}
        <li class="pb-1">
          <a href="/support-tools"class="text-lg {activeUrlButton('support-tools')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <Wrench class={linkIconClasses('support-tools')} />
            <span class="font-medium {linkTextClasses('support-tools')}">Support Tools</span></a
          >
        </li>
        <li class="pb-1">
          <a href="/api-explorer" class="text-lg {activeUrlButton('api-explorer')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <MagnifyingGlass class={linkIconClasses('api-explorer')} />
            <span class="font-medium {linkTextClasses('api-explorer')}">API Explorer</span></a
          >
        </li>
        <li class="pb-1">
          <a href="/localisation" class="text-lg {activeUrlButton('localisation')}" data-sveltekit-preload-data="hover" on:click={drawerClose}>
            <GlobeHemisphereEast class={linkIconClasses('localisation')} />
            <span class="font-medium {linkTextClasses('localisation')}">Localisation</span></a
          >
        </li>
        <li>
          <button
            class="w-full text-left text-lg"
            on:click={() => {
              handleLogout();
            }}
          >
            <SignOut class="text-slate-600" />
            <span class="font-medium text-slate-600">Logout</span>
          </button>
        </li>
      </ul>
    </ul>
  </nav>
</section>
